import { useState, useEffect, useRef } from 'react';
import { apiClient } from '@/lib/api';

/**
 * Custom hook to fetch user data by auth user ID
 * Similar to React Query pattern
 * @param {string} userId - Auth user ID
 * @returns {Object} { data, isLoading, error, refetch }
 */
const useUserData = (userId) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  const fetchUserData = async () => {
    if (!userId) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userData = await apiClient.getUserByUserId(userId);
      setData(userData);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch user data');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchUserData();
  }, [userId]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchUserData
  };
};

export default useUserData;
